// ============================================================================
// src/lib/cattleUnbookedForecast.ts — the cattle "not booked yet" warning
// ----------------------------------------------------------------------------
// Design rule Q14-A (Ronnie's note 9): warn when a forecast month within 8
// weeks has no booked processor date. A forecast month is a VirtualBatch: the
// forecast's own projection of cattle landing in a month nobody has booked.
// Each forecast month is anchored to its 15th (cattleForecast.monthStartMs),
// so "within 8 weeks" means that anchor falls on or before today + 56 days.
// Shown on Cattle > Batches only (Ronnie, 2026-10-09).
// ============================================================================

import {monthLabel, monthStartMs} from './cattleForecast.js';

const WINDOW_DAYS = 56;
const DAY_MS = 86400000;

type ForecastMonth = {monthKey: string; animalIds: readonly unknown[]};

/** Forecast months with cattle, unbooked, whose 15th is within 8 weeks. */
export function unbookedForecastSoon<T extends ForecastMonth>(months: readonly T[], todayMs: number): T[] {
  const limit = todayMs + WINDOW_DAYS * DAY_MS;
  return months.filter((m) => {
    const at = monthStartMs(m.monthKey);
    return at != null && at <= limit && m.animalIds.length > 0;
  });
}

/** "Nov 2026 forecast (6 cows) is not booked with a processor yet." */
export function unbookedForecastMessage(months: readonly ForecastMonth[]): string | null {
  if (!months.length) return null;
  const cows = (m: ForecastMonth) => `${m.animalIds.length} ${m.animalIds.length === 1 ? 'cow' : 'cows'}`;
  if (months.length === 1) {
    return `${monthLabel(months[0].monthKey)} forecast (${cows(months[0])}) is not booked with a processor yet.`;
  }
  const parts = months.map((m) => `${monthLabel(m.monthKey)} (${cows(m)})`);
  return `${parts.slice(0, -1).join(', ')} and ${parts[parts.length - 1]} forecasts are not booked with a processor yet.`;
}
